"use client"

/**
 * Auth-Aware Button Component
 *
 * A button that changes its behavior based on the user's authentication state.
 * Uses Supabase auth cookie detection to determine if user is logged in.
 *
 * When logged in: Shows "Go to Dashboard" -> /app
 * When logged out: Shows specified CTA text -> specified href (default: /setup)
 */
import { useEffect, useState } from "react"
import { hasCookie } from "cookies-next"
import Button, { ButtonVariant, ButtonSize, ButtonContext } from "./Button"

interface AuthAwareButtonProps {
    /** Text to show when user is logged out */
    loggedOutText?: string
    /** Link when user is logged out */
    loggedOutHref?: string
    /** Text to show when user is logged in */
    loggedInText?: string
    /** Link when user is logged in */
    loggedInHref?: string
    /** Button variant */
    variant?: ButtonVariant
    /** Button size */
    size?: ButtonSize
    /** Light or dark background context */
    context?: ButtonContext
    /** Additional CSS classes */
    className?: string
    /** Show arrow icon */
    showArrow?: boolean
}

/**
 * Arrow icon for the button
 */
function ArrowIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={`stroke-current stroke-2 ${className}`} fill="none" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path className="opacity-0 transition group-hover:opacity-100" d="M0 5h7" />
            <path className="transition group-hover:translate-x-[3px]" d="M1 1l4 4-4 4" />
        </svg>
    )
}

export default function AuthAwareButton({ loggedOutText = "Try for Free", loggedOutHref = "/setup", loggedInText = "Go to Dashboard", loggedInHref = "/app", variant = "primary", size = "md", context = "light", className = "", showArrow = false }: AuthAwareButtonProps) {
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Check for Supabase auth cookie
        const cookies = Object.fromEntries(document.cookie.split("; ").map((cookie) => cookie.split("=")))
        const supabaseAuthCookie = Object.keys(cookies).find((name) => /^sb-.+-auth-token$/.test(name))

        setIsLoggedIn(hasCookie("supabase-auth-token") || hasCookie(supabaseAuthCookie))
        setIsLoading(false)
    }, [])

    // Show nothing while loading to prevent flash
    if (isLoading) {
        return <div className={`inline-flex animate-pulse items-center justify-center rounded-button bg-black/10 font-semibold ${size === "sm" ? "h-8 w-24" : size === "lg" ? "h-12 w-36" : "h-10 w-28"} ${className}`} />
    }

    return (
        <Button variant={variant} size={size} context={context} href={isLoggedIn ? loggedInHref : loggedOutHref} className={`group ${className}`} icon={showArrow ? <ArrowIcon /> : undefined} iconPosition="right">
            {isLoggedIn ? loggedInText : loggedOutText}
        </Button>
    )
}
